import { useEffect, useState } from "react";

import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";

import {
  Loader2,
  Package,
  FileText,
  Tag,
  Layers3,
  IndianRupee,
  Percent,
  Hash,
  CalendarDays,
  LayoutGrid,
  Pencil,
  Plus,
} from "lucide-react";

import { useDispatch } from "react-redux";

import { showToast } from "@/redux/Toast/actions";
import { callLoader } from "@/redux/Loader/actions";

import ImageUploadField from "@/components/common/ImageUploadField";
import { insertModule, updateModule } from "@/redux/Modules/modules/actions";
import { getBaseModuleDropdown } from "@/redux/Modules/baseModules/actions";
import { Input, SelectInput } from "../components/FormControls";
import { ModalContent } from "../components/ModalContent";

/* ───────────────────────── INITIAL STATE ───────────────────────── */

const initialState = {
  MODULE_ID: 0,
  BASEMODULE_ID: 0,

  MODULE_NAME: "",
  MODULE_DESCRIPTION: "",
  MODULE_TYPE: "",
  MODULE_PRICINGMODEL: "",

  MODULE_BASEPRICE: 0,
  MODULE_BASEPRICETYPE: "PER MONTH",
  MODULE_PERSEATPRICE: 0,
  MODULE_USAGERATE: 0,

  TRIAL_DAYS: 0,
  SEQUENCE_NO: 0,
  CREATED_BY: "admin",

  FILEDATA: null,
};

export default function AddModuleModal({
  open,
  onOpenChange,
  isEdit = false,
  editData = null,
  onSuccess,
  baseModulesDropdown,
}) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialState);
  const [previewImage, setPreviewImage] = useState(null);
  const [errors, setErrors] = useState({});

  /* ───────────── RESET + PREFILL ───────────── */

  useEffect(() => {
    if (!open) {
      setFormData(initialState);
      setPreviewImage(null);
      setErrors({});
      return;
    }

    if (isEdit && editData) {
      setFormData({
        MODULE_ID: editData?.MODULE_ID || 0,
        BASEMODULE_ID: editData?.BASEMODULE_ID || 0,

        MODULE_NAME: editData?.MODULE_NAME || "",
        MODULE_DESCRIPTION: editData?.MODULE_DESCRIPTION || "",
        MODULE_TYPE: editData?.MODULE_TYPE || "",
        MODULE_PRICINGMODEL: editData?.MODULE_PRICINGMODEL || "",

        MODULE_BASEPRICE: editData?.MODULE_BASEPRICE || 0,
        MODULE_BASEPRICETYPE: editData?.MODULE_BASEPRICETYPE || "PER MONTH",
        MODULE_PERSEATPRICE: editData?.MODULE_PERSEATPRICE || 0,
        MODULE_USAGERATE: editData?.MODULE_USAGERATE || 0,

        TRIAL_DAYS: editData?.MODULE_TRIAL_DAYS || 0,
        SEQUENCE_NO: editData?.MODULE_SEQUENCE_NO || 0,
        CREATED_BY: editData?.MODULE_CREATED_BY || "admin",

        FILEDATA: null,
      });

      setPreviewImage(editData?.MODULE_LOGO || null);
    }
  }, [open, isEdit, editData]);

  const NUMERIC_FIELDS = new Set([
    "BASEMODULE_ID",
    "MODULE_BASEPRICE",
    "MODULE_PERSEATPRICE",
    "MODULE_USAGERATE",
    "TRIAL_DAYS",
    "SEQUENCE_NO",
  ]);

  /* ───────────── HANDLE CHANGE ───────────── */

  const handleChange = (key, value) => {
    const finalValue = NUMERIC_FIELDS.has(key)
      ? value === ""
        ? ""
        : Number(value)
      : value;

    setFormData((prev) => ({
      ...prev,
      [key]: finalValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [key]: "",
    }));
  };
  /* ───────────── VALIDATION ───────────── */

  const validate = () => {
    const err = {};

    if (!formData.BASEMODULE_ID) err.BASEMODULE_ID = "Base Module is required";
    if (!formData.MODULE_NAME) err.MODULE_NAME = "Module name is required";
    if (!formData.MODULE_TYPE) err.MODULE_TYPE = "Module type is required";

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* ───────────── SUBMIT ───────────── */

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      dispatch(callLoader(true));

      const payload = new FormData();
      console.log("formDAta ==========", formData);
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value ?? "");
      });

      const action = isEdit ? updateModule : insertModule;

      await dispatch(action(payload));

      dispatch(
        showToast(
          isEdit
            ? "Module updated successfully"
            : "Module created successfully",
          "success",
          true,
        ),
      );

      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      dispatch(
        showToast(err?.response?.data?.message || err.message, "error", true),
      );
    } finally {
      setLoading(false);
      dispatch(callLoader(false));
    }
  };

  /* ───────────── UI ───────────── */

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ModalContent className="h-[90vh] flex flex-col">
        {/* HEADER */}
        <DialogHeader className="shrink-0 relative border-b border-white/10 px-6 py-5 bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900">
          <div className="flex items-center gap-4">
            <div
              className="h-11 w-11 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center
        shadow-lg shadow-teal-500/20
        shrink-0
      "
            >
              {isEdit ? (
                <Pencil className="h-5 w-5 text-white" />
              ) : (
                <Plus className="h-5 w-5 text-white" />
              )}
            </div>

            <div>
              <DialogTitle className="text-xl font-semibold text-white">
                {isEdit ? "Update Module" : "Create Module"}
              </DialogTitle>

              <DialogDescription className="mt-1 text-sm text-slate-300">
                Manage module configuration and pricing
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* BODY */}
        <div className="flex-1 grid grid-cols-1 gap-6 overflow-y-auto p-6 lg:grid-cols-2">
          {/* LEFT */}
          <div className="space-y-4">
            <SelectInput
              label="Base Module"
              name="BASEMODULE_ID"
              icon={LayoutGrid}
              value={formData.BASEMODULE_ID}
              //   onValueChange={(name, value) => handleChange(name, Number(value))}
              onValueChange={handleChange}
              error={errors.BASEMODULE_ID}
              options={[
                { value: "", label: "Select Base Module" },
                ...baseModulesDropdown?.map((item) => ({
                  value: item.VALUE,
                  label: item.TEXT,
                })),
              ]}
            />
            <Input
              label="Module Name"
              name="MODULE_NAME"
              icon={Package}
              value={formData.MODULE_NAME}
              onValueChange={handleChange}
              error={errors.MODULE_NAME}
            />

            <Input
              label="Description"
              name="MODULE_DESCRIPTION"
              textarea
              icon={FileText}
              value={formData.MODULE_DESCRIPTION}
              onValueChange={handleChange}
            />

            <SelectInput
              label="Module Type"
              name="MODULE_TYPE"
              icon={Tag}
              value={formData.MODULE_TYPE}
              onValueChange={handleChange}
              error={errors.MODULE_TYPE}
              placeholder="Select Module Type"
              options={[
                { value: "CORE", label: "CORE" },
                { value: "ADDON", label: "ADDON" },
              ]}
            />

            {/* <Input
              label="Type"
              icon={Tag}
              value={formData.MODULE_TYPE}
              onChange={(e) => handleChange("MODULE_TYPE", e.target.value)}
              error={errors.MODULE_TYPE}
            /> */}

            <Input
              label="Pricing Model"
              name="MODULE_PRICINGMODEL"
              icon={Layers3}
              value={formData.MODULE_PRICINGMODEL}
              onValueChange={handleChange}
            />

            <ImageUploadField
              label="Module Logo"
              value={previewImage}
              onChange={(preview, file) => {
                setPreviewImage(preview);
                handleChange("FILEDATA", file);
              }}
            />
          </div>

          {/* RIGHT */}
          <div className="space-y-4">
            <Input
              label="Base Price"
              name="MODULE_BASEPRICE"
              icon={IndianRupee}
              type="number"
              value={formData.MODULE_BASEPRICE}
              onValueChange={handleChange}
            />

            <Input
              label="Price Type"
              name="MODULE_BASEPRICETYPE"
              value={formData.MODULE_BASEPRICETYPE}
              onValueChange={handleChange}
            />

            <Input
              label="Per Seat Price"
              name="MODULE_PERSEATPRICE"
              icon={IndianRupee}
              type="number"
              value={formData.MODULE_PERSEATPRICE}
              onValueChange={handleChange}
            />

            <Input
              label="Usage Rate"
              name="MODULE_USAGERATE"
              icon={Percent}
              type="number"
              value={formData.MODULE_USAGERATE}
              onValueChange={handleChange}
            />

            <Input
              label="Sequence No"
              name="SEQUENCE_NO"
              icon={Hash}
              type="number"
              value={formData.SEQUENCE_NO}
              onValueChange={handleChange}
            />

            <Input
              label="Trial Days"
              name="TRIAL_DAYS"
              icon={CalendarDays}
              type="number"
              value={formData.TRIAL_DAYS}
              onValueChange={handleChange}
            />
          </div>
        </div>

        {/* FOOTER */}
        <DialogFooter className="shrink-0 border-t border-slate-200 dark:border-slate-800 px-6 py-4 bg-slate-50 dark:bg-slate-900/50">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-teal-500 text-white"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isEdit ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </DialogFooter>
      </ModalContent>
    </Dialog>
  );
}
